// Session-identity profile API — talks to lendcord-core directly (see
// webpack.config.js's devServer.proxy for /profile). Bearer-authenticated.
// Page-specific profile concerns (e.g. document downloads) live in
// features/profile/api/profile.api.ts instead — this file only owns the
// identity data shared across the app shell (TopBar) and the profile page.

import { baseApi } from "@/app/store/baseApi";
import type {
  Address,
  AddressType,
  BankDetail,
  EmploymentDetail,
  EmploymentDetailRequest,
  EnterpriseOption,
  JoinEnterpriseRequestView,
  Nominee,
  NomineeRequest,
  Profile,
  RevealResponse,
  RoleUpgradeRequest,
  UpdateProfileIdentityRequest,
  UpsertAddressRequest,
  UpsertBankDetailRequest,
} from "@/app/profile/profile.types";

export const profileApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getProfile: builder.query<Profile, void>({
      query: () => ({ url: "/profile", auth: true }),
      providesTags: ["Profile"],
    }),

    // POST /profile/role-upgrade-request — LENDER self-serve upgrade
    // request. Returns the updated roleUpgradeRequest view (same field
    // shape already on GET /profile), so invalidating "Profile" lets the
    // ProfileIdentityCard react to the new status without a separate refetch.
    requestRoleUpgrade: builder.mutation<RoleUpgradeRequest, void>({
      query: () => ({ url: "/profile/role-upgrade-request", method: "POST", auth: true }),
      invalidatesTags: ["Profile"],
    }),

    // GET /profile/enterprises — the join-enterprise picklist, callable
    // regardless of allowJoinEnterprise (defensive UX only, the apply
    // mutation below is the real gate). No tag/invalidation — this list
    // isn't affected by anything else this app does.
    listAvailableEnterprises: builder.query<EnterpriseOption[], void>({
      query: () => ({ url: "/profile/enterprises", auth: true }),
    }),

    // POST /profile/join-enterprise-request — self-serve apply, creates or
    // restarts (reapply) the one-row-per-user cycle. Returns the updated
    // joinEnterpriseRequest view (same shape GET /profile carries), so
    // invalidating "Profile" lets the caller react to the new status
    // without a separate refetch — same pattern as requestRoleUpgrade above.
    requestJoinEnterprise: builder.mutation<JoinEnterpriseRequestView, { enterpriseId: string }>({
      query: (body) => ({ url: "/profile/join-enterprise-request", method: "POST", body, auth: true }),
      invalidatesTags: ["Profile"],
    }),

    // Read-only reveal — doesn't change server state, so no invalidation.
    revealPii: builder.mutation<RevealResponse, { dataType: string; userId: string }>({
      query: ({ dataType, userId }) => ({
        url: "/profile/reveal",
        method: "POST",
        body: { dataType, value: userId },
        auth: true,
      }),
    }),

    updateProfileIdentity: builder.mutation<Profile, UpdateProfileIdentityRequest>({
      query: (payload) => ({ url: "/profile", method: "POST", body: payload, auth: true }),
      invalidatesTags: ["Profile"],
    }),

    upsertAddress: builder.mutation<Address, { type: AddressType; payload: UpsertAddressRequest }>({
      query: ({ type, payload }) => ({
        url: `/profile/addresses/${type}`,
        method: "PUT",
        body: payload,
        auth: true,
      }),
      invalidatesTags: ["Profile"],
    }),

    upsertPrimaryBankDetail: builder.mutation<BankDetail, UpsertBankDetailRequest>({
      query: (payload) => ({ url: "/profile/bank-details/primary", method: "PUT", body: payload, auth: true }),
      invalidatesTags: ["Profile"],
    }),

    // Genuine create — 409 if a nominee already exists for this account. Use
    // updateNominee instead once one exists (see GET /profile's nominee field).
    createNominee: builder.mutation<Nominee, NomineeRequest>({
      query: (payload) => ({ url: "/profile/nominee", method: "POST", body: payload, auth: true }),
      invalidatesTags: ["Profile"],
    }),

    // Genuine update — 404 if no nominee exists yet.
    updateNominee: builder.mutation<Nominee, NomineeRequest>({
      query: (payload) => ({ url: "/profile/nominee", method: "PUT", body: payload, auth: true }),
      invalidatesTags: ["Profile"],
    }),

    // Genuine create — 409 if an employment record already exists for this
    // account. Use updateEmploymentDetail instead once one exists.
    createEmploymentDetail: builder.mutation<EmploymentDetail, EmploymentDetailRequest>({
      query: (payload) => ({ url: "/profile/employment", method: "POST", body: payload, auth: true }),
      invalidatesTags: ["Profile"],
    }),

    // Genuine update — 404 if no employment record exists yet.
    updateEmploymentDetail: builder.mutation<EmploymentDetail, EmploymentDetailRequest>({
      query: (payload) => ({ url: "/profile/employment", method: "PUT", body: payload, auth: true }),
      invalidatesTags: ["Profile"],
    }),

    deleteNominee: builder.mutation<void, void>({
      query: () => ({ url: "/profile/nominee", method: "DELETE", auth: true }),
      invalidatesTags: ["Profile"],
    }),

    deleteEmploymentDetail: builder.mutation<void, void>({
      query: () => ({ url: "/profile/employment", method: "DELETE", auth: true }),
      invalidatesTags: ["Profile"],
    }),

    // Self-serve account deletion — deactivates the account and revokes the
    // token that authenticated this call (same as /auth/logout), so the
    // caller must locally log out right after this resolves; the token is
    // dead server-side the instant this returns. No invalidation needed —
    // nothing stays mounted to read the Profile tag past that point.
    deleteAccount: builder.mutation<void, void>({
      query: () => ({ url: "/profile", method: "DELETE", auth: true }),
    }),
  }),
});

export const {
  useGetProfileQuery,
  useRequestRoleUpgradeMutation,
  useListAvailableEnterprisesQuery,
  useRequestJoinEnterpriseMutation,
  useRevealPiiMutation,
  useUpdateProfileIdentityMutation,
  useUpsertAddressMutation,
  useUpsertPrimaryBankDetailMutation,
  useCreateNomineeMutation,
  useUpdateNomineeMutation,
  useCreateEmploymentDetailMutation,
  useUpdateEmploymentDetailMutation,
  useDeleteNomineeMutation,
  useDeleteEmploymentDetailMutation,
  useDeleteAccountMutation,
} = profileApi;
